import React, { useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Typography,
    Button,
    Checkbox,
    IconButton,
    Card,
    CardContent,
    Select,
    MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DeleteIcon from '@mui/icons-material/Delete';
import AddressForm from '../../components/address-form/AddressForm';

function PersonalInfo({ onNext }) {
    const [personalInfo, setPersonalInfo] = useState({
        firstName: '',
        middleName: '',
        lastName: '',
        dob: null,
        gender: '',
        sin: '',
        email: '',
        phone: '',
        emergencyContactName: '',
        emergencyContactRelationship: '',
        emergencyContactPhone: '',
        fluentEnglish: false,
        permissionToContact: false,
    });

    const [addresses, setAddresses] = useState([
        {
            line1: '',
            line2: '',
            city: '',
            stateProvince: '',
            postalCode: '',
            country: '',
            fromDate: null,
            toDate: null,
        }
    ]);

    const handlePersonalInfoChange = (field) => (event) => {
        setPersonalInfo({
            ...personalInfo,
            [field]: event.target.value
        });
    };


    const addAddress = () => {
        setAddresses([...addresses, {
            line1: '',
            line2: '',
            city: '',
            stateProvince: '',
            postalCode: '',
            country: '',
            fromDate: null,
            toDate: null,
        }]);
    };

    const removeAddress = (index) => {
        const newAddresses = addresses.filter((_, i) => i !== index);
        setAddresses(newAddresses);
    };

    const handleSubmit = () => {
        // Validation would go here
        onNext();
    };
    const handleAddressFieldChange = (index) => (field, value) => {
        const newAddresses = [...addresses];
        newAddresses[index] = {
            ...newAddresses[index],
            [field]: value
        };
        setAddresses(newAddresses);
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
            <Typography variant="h6" gutterBottom>Personal Information</Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="First Name"
                        value={personalInfo.firstName}
                        onChange={handlePersonalInfoChange('firstName')}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Middle Name"
                        value={personalInfo.middleName}
                        onChange={handlePersonalInfoChange('middleName')}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Last Name"
                        value={personalInfo.lastName}
                        onChange={handlePersonalInfoChange('lastName')}
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Date of Birth"
                            value={personalInfo.dob}
                            onChange={(newValue) => setPersonalInfo({ ...personalInfo, dob: newValue })}
                            renderInput={(params) => <TextField {...params} fullWidth required />}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl component="fieldset">
                        <RadioGroup
                            row
                            value={personalInfo.gender}
                            onChange={handlePersonalInfoChange('gender')}
                        >
                            <FormControlLabel value="female" control={<Radio />} label="Female" />
                            <FormControlLabel value="male" control={<Radio />} label="Male" />
                            <FormControlLabel value="other" control={<Radio />} label="Other" />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                {/* Contact Information */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="SIN"
                        value={personalInfo.sin}
                        onChange={handlePersonalInfoChange('sin')}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type="email"
                        label="Email"
                        value={personalInfo.email}
                        onChange={handlePersonalInfoChange('email')}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Phone"
                        value={personalInfo.phone}
                        onChange={handlePersonalInfoChange('phone')}
                        required
                    />
                </Grid>

                {/* Emergency Contact */}
                <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>Emergency Contact</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Name"
                        value={personalInfo.emergencyContactName}
                        onChange={handlePersonalInfoChange('emergencyContactName')}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Relationship"
                        value={personalInfo.emergencyContactRelationship}
                        onChange={handlePersonalInfoChange('emergencyContactRelationship')}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Phone"
                        value={personalInfo.emergencyContactPhone}
                        onChange={handlePersonalInfoChange('emergencyContactPhone')}
                        required
                    />
                </Grid>

                {/* Checkboxes */}
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={personalInfo.fluentEnglish}
                                onChange={(e) => setPersonalInfo({ ...personalInfo, fluentEnglish: e.target.checked })}
                            />
                        }
                        label="I can speak, write, and read English fluently"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={personalInfo.permissionToContact}
                                onChange={(e) => setPersonalInfo({ ...personalInfo, permissionToContact: e.target.checked })}
                            />
                        }
                        label="I give permission to contact me via text or phone regarding this application"
                    />
                </Grid>

                {/* Address History */}
                <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                        Address History (Last 3 Years)
                        <Button sx={{ ml: 2 }} variant="outlined" size="small" onClick={addAddress}>
                            Add Address
                        </Button>
                    </Typography>
                </Grid>

                {addresses.map((address, index) => (
                    <Grid item xs={12} key={index}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2">Address {index + 1}</Typography>
                                    {addresses.length > 1 && (
                                        <IconButton onClick={() => removeAddress(index)} size="small">
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </Box>
                                <AddressForm
                                    address={address}
                                    onChange={handleAddressFieldChange(index)}
                                />
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid item xs={12} sm={6}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                label="From Date"
                                                value={address.fromDate}
                                                onChange={(newValue) => handleAddressFieldChange(index)('fromDate', newValue)}
                                                renderInput={(params) => <TextField {...params} fullWidth required />}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                label="To Date"
                                                value={address.toDate}
                                                onChange={(newValue) => handleAddressFieldChange(index)('toDate', newValue)}
                                                renderInput={(params) => <TextField {...params} fullWidth required />}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}

                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleSubmit}
                        sx={{ mt: 3 }}
                    >
                        Continue
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
}

export default PersonalInfo;
